import type { Metro, State, Store } from '../../../clinics/clinics';

export function getDistanceInKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

export const KM_PER_MILE = 1.60934;

export function getMetroCenter(metro: Metro): { lat: number; long: number } | null {
    const validStores = metro.stores.filter(
        (s) => Number.isFinite(parseFloat(s.geo?.lat)) && Number.isFinite(parseFloat(s.geo?.long))
    );
    if (!validStores.length) return null;
    const avgLat = validStores.reduce((sum, s) => sum + parseFloat(s.geo.lat), 0) / validStores.length;
    const avgLong = validStores.reduce((sum, s) => sum + parseFloat(s.geo.long), 0) / validStores.length;
    return { lat: avgLat, long: avgLong };
}

const MAX_NEARBY_METRO_DISTANCE_MILES = 200;

export function getClosestMetros(
    currentMetro: Metro,
    allStates: State[],
    limitMiles = MAX_NEARBY_METRO_DISTANCE_MILES,
    limit = 8
): Array<{ metro: Metro; state: State }> {
    const currentCenter = getMetroCenter(currentMetro);
    if (!currentCenter) return [];

    const maxDistanceKm = limitMiles * KM_PER_MILE;
    const results: Array<{ metro: Metro; state: State; distance: number }> = [];

    for (const state of allStates) {
        for (const metro of state.metros) {
            if (metro.pathname === currentMetro.pathname) continue;

            const center = getMetroCenter(metro);
            if (!center) continue;

            const distance = getDistanceInKm(currentCenter.lat, currentCenter.long, center.lat, center.long);
            if (distance <= maxDistanceKm) {
                results.push({ metro, state, distance });
            }
        }
    }

    return results
        .sort((a, b) => a.distance - b.distance)
        .slice(0, limit)
        .map(({ metro, state }) => ({ metro, state }));
}

const MAX_NEARBY_STORE_DISTANCE_MILES = 50;

export function getClosestStores(
    lat: string | null,
    long: string | null,
    stores: Store[],
    excludeCity: string,
    limit = 8
): Store[] {
    const filteredStores = stores.filter((store) => store.marketingCity.toLowerCase() !== excludeCity.toLowerCase());

    const currentLat = lat ? parseFloat(lat) : null;
    const currentLong = long ? parseFloat(long) : null;

    const hasValidGeo = currentLat != null && currentLong != null;
    const allStoresHaveGeo = filteredStores.every((store) => store.geo?.lat != null && store.geo?.long != null);

    if (hasValidGeo && allStoresHaveGeo) {
        const maxDistanceKm = MAX_NEARBY_STORE_DISTANCE_MILES * KM_PER_MILE;
        return filteredStores
            .map((store) => ({
                ...store,
                distance: getDistanceInKm(
                    currentLat!,
                    currentLong!,
                    parseFloat(store.geo.lat),
                    parseFloat(store.geo.long)
                ),
            }))
            .filter((store) => store.distance <= maxDistanceKm)
            .sort((a, b) => a.distance - b.distance)
            .slice(0, limit);
    }

    // Fallback: sort alphabetically by marketingCity
    return filteredStores.sort((a, b) => a.marketingCity.localeCompare(b.marketingCity)).slice(0, limit);
}
